/**
 * Brand portal requests and results of AI 文案助手 (copy, including a 推廣活動's name, description and terms) and
 * AI 三語翻譯 (translate). The banner pair of AI 生成圖片 needs nothing new: the brand portal asks /ai/images for both shapes.
 */
import { z } from 'zod';
import { LANGS, type LocalizedText } from './i18n.ts';

/** Tones of AI copy; a 推廣活動 keeps the one picked, and its 「AI 生成圖片」 follows it. */
export const AI_COPY_TONES = ['warm', 'fun', 'lively', 'professional', 'elegant', 'simple'] as const;
export type AiCopyTone = (typeof AI_COPY_TONES)[number];
export const AI_COPY_TONE_LABELS: Record<AiCopyTone, string> = { warm: '溫暖', fun: '有趣', lively: '活潑', professional: '專業', elegant: '高雅', simple: '簡潔' };
export const isAiCopyTone = (v: unknown): v is AiCopyTone => (AI_COPY_TONES as readonly unknown[]).includes(v);

export const AI_COPY_LENGTHS = ['short', 'medium', 'long'] as const;
export type AiCopyLength = (typeof AI_COPY_LENGTHS)[number];
export const AI_COPY_LENGTH_LABELS: Record<AiCopyLength, string> = { short: '簡短', medium: '適中', long: '詳細' };

export const aiCopySchema = z.object({
  /** What the field is, given by the page (e.g. 「最新消息標題」). */
  purpose: z.string().trim().min(2).max(60),
  /** What to say, typed by the user. */
  brief: z.string().trim().min(2).max(500),
  tone: z.enum(AI_COPY_TONES),
  length: z.enum(AI_COPY_LENGTHS),
  /** The language tab being edited. */
  lang: z.enum(LANGS),
  /** Paragraphs allowed (body text); otherwise one line (titles). */
  multiline: z.boolean().optional(),
  /** The field's character limit. */
  maxLength: z.number().int().min(10).max(20000).optional(),
  /** Facts or rules from the page, e.g. the title of the news item or the placeholders a message may use. */
  context: z.string().trim().max(300).optional(),
});
export type AiCopyInput = z.infer<typeof aiCopySchema>;

export interface AiCopyResult {
  /** Two or three drafts to pick from. */
  options: string[];
}

/** 「AI 生成內容」 on a 推廣活動: its name, description and terms in each of the brand's languages, from its settings. */
export const aiPromoCopySchema = z.object({
  tone: z.enum(AI_COPY_TONES),
  langs: z.array(z.enum(LANGS)).min(1).max(LANGS.length),
  /** The campaign's settings in words (period, scope, conditions and gifts, limits), written by the page. */
  facts: z.string().trim().min(10).max(3000),
});
export type AiPromoCopyInput = z.infer<typeof aiPromoCopySchema>;

export interface AiPromoCopyResult {
  name: LocalizedText;
  description: LocalizedText;
  terms: LocalizedText;
}

/** Most text one 「AI 翻譯」 may write: source characters times the languages each field goes to. */
export const AI_TRANSLATE_MAX_CHARS = 6000;
/** Fields one translation request takes. */
export const AI_TRANSLATE_MAX_FIELDS = 100;

/** Characters one translation request writes, for AI_TRANSLATE_MAX_CHARS. */
export const translateLoad = (fields: ReadonlyArray<{ text: string; to: readonly unknown[] }>) => fields.reduce((n, f) => n + f.text.length * f.to.length, 0);

export const aiTranslateSchema = z
  .object({
    from: z.enum(LANGS),
    fields: z
      .array(
        z.object({
          /** The field's label, so the model knows what the text is. */
          label: z.string().trim().max(60).optional(),
          text: z.string().trim().min(1).max(AI_TRANSLATE_MAX_CHARS),
          /** Rich text: tags and links are kept, only the text between them is translated. */
          html: z.boolean().optional(),
          maxLength: z.number().int().min(1).max(20000).optional(),
          /** Languages to fill for this field. */
          to: z.array(z.enum(LANGS)).min(1).max(2),
        }),
      )
      .min(1)
      .max(AI_TRANSLATE_MAX_FIELDS),
  })
  .superRefine((v, ctx) => {
    if (v.fields.some((f) => f.to.includes(v.from))) ctx.addIssue({ code: 'custom', path: ['fields'], message: 'A field cannot be translated into its own language' });
    if (translateLoad(v.fields) > AI_TRANSLATE_MAX_CHARS) ctx.addIssue({ code: 'custom', path: ['fields'], message: `Too much text for one translation (over ${AI_TRANSLATE_MAX_CHARS} characters)` });
  });
export type AiTranslateInput = z.infer<typeof aiTranslateSchema>;

export interface AiTranslateResult {
  /** One entry per field asked, in the same order; a language is missing when its translation could not be used. */
  fields: LocalizedText[];
}
